import jsPDF from "jspdf";

export function generateJaybeeReport(session, narrative, documents, verifications, walletScreenings = [], sowSession = null, returnBlob = false) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, H = 297;
  const ml = 20, mr = 20, mt = 20;
  const contentW = W - ml - mr;
  let y = mt;

  const colors = {
    black: [0, 0, 0],
    darkGrey: [60, 60, 60],
    midGrey: [120, 120, 120],
    lightGrey: [200, 200, 200],
    veryLight: [245, 245, 245],
    white: [255, 255, 255],
    accent: [40, 40, 80],
  };

  const checkPage = (needed = 10) => {
    if (y + needed > H - 20) { doc.addPage(); y = mt; }
  };

  const setFont = (size, style = "normal", color = colors.black) => {
    doc.setFontSize(size);
    doc.setFont("helvetica", style);
    doc.setTextColor(...color);
  };

  const addText = (text, x, fontSize = 10, style = "normal", color = colors.black, maxWidth = contentW) => {
    setFont(fontSize, style, color);
    const lines = doc.splitTextToSize(String(text || "—"), maxWidth);
    checkPage(lines.length * (fontSize * 0.4 + 1));
    doc.text(lines, x, y);
    y += lines.length * (fontSize * 0.4 + 1) + 1;
    return lines.length;
  };

  const addParagraph = (text, label = null) => {
    if (label) {
      checkPage(8);
      setFont(9, "bold", colors.darkGrey);
      doc.text(label, ml, y);
      y += 5;
    }
    setFont(10, "normal", colors.black);
    const lines = doc.splitTextToSize(String(text || "—"), contentW);
    checkPage(lines.length * 5 + 4);
    doc.text(lines, ml, y);
    y += lines.length * 5 + 4;
  };

  const addSection = (title) => {
    checkPage(14);
    y += 4;
    // Section rule
    doc.setDrawColor(...colors.accent);
    doc.setLineWidth(0.5);
    doc.line(ml, y, W - mr, y);
    y += 5;
    setFont(11, "bold", colors.accent);
    doc.text(title, ml, y);
    y += 6;
  };

  const addKeyValue = (label, value, indent = ml) => {
    const val = value && String(value).trim() && String(value) !== "[object Object]" ? String(value) : null;
    if (!val) return; // Skip empty fields
    checkPage(6);
    setFont(9, "bold", colors.darkGrey);
    doc.text(label + ":", indent, y);
    setFont(9, "normal", colors.black);
    const valLines = doc.splitTextToSize(val, contentW - 50);
    doc.text(valLines, indent + 50, y);
    y += Math.max(valLines.length * 4.5, 5);
  };

  const report = session.report || {};
  const ref = narrative.reference || `SCM-${new Date().getFullYear()}-001`;
  const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  // ── HEADER ──────────────────────────────────────────────
  doc.setFillColor(...colors.accent);
  doc.rect(0, 0, W, 28, "F");

  doc.setTextColor(...colors.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("SAGE CAPITAL MANAGEMENT", ml, 11);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("CLIENT ACCEPTANCE REPORT — CONFIDENTIAL", ml, 18);
  doc.setFontSize(9);
  doc.text(`Ref: ${ref}`, W - mr - doc.getTextWidth(`Ref: ${ref}`), 11);
  doc.text(`Date: ${today}`, W - mr - doc.getTextWidth(`Date: ${today}`), 18);
  y = 36;

  // ── HEADER META ─────────────────────────────────────────
  doc.setFillColor(...colors.veryLight);
  doc.setDrawColor(...colors.lightGrey);
  doc.rect(ml, y, contentW, 26, "FD");
  y += 5;
  setFont(9, "normal", colors.darkGrey);
  const metaLeft = ml + 4;
  addKeyValue("Prepared by", "Ross Griffiths, Compliance Manager — Sage Capital Management", metaLeft);
  addKeyValue("Classification", "Confidential — Client Due Diligence Report", metaLeft);
  const cdMeta = report.companyDetails || {};
  addKeyValue("Client", (session.company_name || session.contact_name) + (cdMeta.jurisdiction || report.jurisdiction ? ' · ' + (cdMeta.jurisdiction || report.jurisdiction) : ''), metaLeft);
  y += 4;

  // ── RECOMMENDATION BANNER ───────────────────────────────
  checkPage(16);
  const decision = session.mlro_decision || "Pending";
  const bannerColor = decision === "Approve" ? [34, 139, 34] : decision === "Decline" ? [180, 0, 0] : [180, 120, 0];
  doc.setFillColor(...bannerColor);
  doc.rect(ml, y, contentW, 12, "F");
  setFont(12, "bold", colors.white);
  const bannerText = decision === "Approve" ? "RECOMMENDATION: APPROVE" : decision === "Decline" ? "RECOMMENDATION: DECLINE" : "RECOMMENDATION: PENDING REVIEW";
  doc.text(bannerText, W / 2 - doc.getTextWidth(bannerText) / 2, y + 8);
  y += 16;

  // ── 1. EXECUTIVE SUMMARY ────────────────────────────────
  addSection("1. Executive Summary");
  addParagraph(narrative.executiveSummary);

  // ── AML COMPLIANCE SUMMARY TABLE ────────────────────────
  checkPage(80);
  y += 4;
  setFont(10, "bold", colors.accent);
  doc.text("AML Compliance Summary", ml, y);
  y += 6;

  const sumsubOk = verifications.some(v => v.status === "approved");
  const allDocsOk = documents.length === 0 || documents.every(d => d.assessment !== "Unsatisfactory");
  const hasWallets = walletScreenings.length > 0 || (report.travelRule?.wallets || []).length > 0;
  const tr = report.travelRule;
  const trOk = !hasWallets || (tr && tr.overallStatus === "compliant");
  const hasQualifyingUbos = (report.ubos || []).some(u => parseFloat(u.ownership) >= 25);
  const sowWaived = !!report.sowWaiver;
  const sowOk = sowWaived || !hasQualifyingUbos || (report.ubos || []).filter(u => parseFloat(u.ownership) >= 25).every(u => u.sowAssessment || u.sowSignedOff);

  const checks = [
    ["Contracting party identified", sumsubOk, sumsubOk ? "Sumsub verified" : "Verification pending"],
    ["Beneficial ownership established", (report.ubos || []).length > 0, (report.ubos || []).length > 0 ? `${report.ubos.length} UBO(s) documented` : "No UBOs recorded"],
    ["Tax compliance declaration", !!(report.taxCompliance?.declared), report.taxCompliance?.declared ? "Declaration obtained from client" : report.taxCompliance?.riskIndicatorsPresent ? "Risk indicators present" : "Not collected"],
    ["Risk classification", !!report.riskTier, report.riskTier ? `${report.riskTier} risk — rationale documented` : "Not assigned"],
    ["PEP / sanctions screening", sumsubOk, sumsubOk ? "Clear — Sumsub" : "Pending"],
    ["FATF jurisdiction assessment", !(report.fatfFlags?.entityJurisdictionFlagged), !report.fatfFlags ? "Pre-dates FATF check — manually verified" : report.fatfFlags.entityJurisdictionFlagged ? `${report.fatfFlags.fatfStatus || "Flagged"} — EDD applied` : "Clean — no flags"],
    ["Source of funds", !!(report.sofAssessment?.narrative || report.sofAssessment?.fundingRoute), report.sofAssessment?.fundingRoute || report.sofAssessment?.narrative ? "Documented" : "Not documented"],
    ["Source of wealth (UBOs 25%+)", sowOk, sowWaived ? `Not required — MLRO waiver: ${report.sowWaiver.reason || ""}` : !hasQualifyingUbos ? "No qualifying UBOs" : sowOk ? "All UBOs interviewed & assessed" : "Some UBOs pending"],
    ["Chainalysis wallet screening", !hasWallets || walletScreenings.length > 0, !hasWallets ? "No wallets provided" : `${walletScreenings.length} wallet(s) screened`],
    ["Travel Rule (FINMA 02/2019)", trOk, !hasWallets ? "No crypto wallets — not applicable" : trOk ? "All wallets verified" : "Wallets pending verification"],
    ["Documents on file", allDocsOk, documents.length > 0 ? `${documents.length} document(s) — ${allDocsOk ? "all satisfactory" : "review required"}` : "No documents uploaded"],
    ["MLRO internal sign-off", !!(session.mlro_decision), session.mlro_decision ? `Approved by Compliance Manager` : "Pending"],
  ];

  // Table header
  const colW = [contentW * 0.5, contentW * 0.1, contentW * 0.4];
  const colX = [ml, ml + colW[0], ml + colW[0] + colW[1]];
  const rowH = 7;

  doc.setFillColor(...colors.accent);
  doc.rect(ml, y, contentW, rowH, "F");
  setFont(8, "bold", colors.white);
  doc.text("AML Requirement", colX[0] + 2, y + 5);
  doc.text("Status", colX[1] + 2, y + 5);
  doc.text("Evidence / Notes", colX[2] + 2, y + 5);
  y += rowH;

  checks.forEach(([label, ok, note], idx) => {
    checkPage(rowH + 2);
    // Alternating row background
    if (idx % 2 === 0) {
      doc.setFillColor(248, 249, 250);
      doc.rect(ml, y, contentW, rowH, "F");
    }
    // Cell borders
    doc.setDrawColor(...colors.lightGrey);
    doc.setLineWidth(0.1);
    doc.rect(ml, y, contentW, rowH, "S");

    setFont(8, "normal", colors.black);
    doc.text(doc.splitTextToSize(label, colW[0] - 4)[0], colX[0] + 2, y + 5);

    // Tick or cross
    if (ok) {
      doc.setFillColor(34, 139, 34);
      doc.roundedRect(colX[1] + 3, y + 1.5, 8, 5, 1, 1, "F");
      setFont(7, "bold", [255,255,255]);
      doc.text("✓", colX[1] + 5.5, y + 5.2);
    } else {
      doc.setFillColor(180, 0, 0);
      doc.roundedRect(colX[1] + 3, y + 1.5, 8, 5, 1, 1, "F");
      setFont(7, "bold", [255,255,255]);
      doc.text("✗", colX[1] + 5.5, y + 5.2);
    }

    setFont(8, "normal", colors.darkGrey);
    doc.text(doc.splitTextToSize(String(note || "—"), colW[2] - 4)[0], colX[2] + 2, y + 5);
    y += rowH;
  });
  y += 6;

  // ── 2. CLIENT PROFILE ───────────────────────────────────
  addSection("2. Client Profile");
  addParagraph(narrative.clientProfile);
  y += 2;
  const cd = report.companyDetails || {};
  addKeyValue("Legal Name", cd.legalName || report.legalName || session.company_name);
  addKeyValue("Incorporated", cd.dateOfIncorporation);
  addKeyValue("Jurisdiction", cd.jurisdiction || report.jurisdiction);
  addKeyValue("Registration No.", cd.registrationNumber || report.registrationNumber);
  addKeyValue("Registered Address", cd.registeredAddress || report.registeredAddress);
  addKeyValue("Business Description", cd.businessDescription || report.businessDescription);
  if (report.objectAndPurpose?.object || report.objectAndPurpose?.purpose) {
    y += 2;
    addKeyValue("Object of Relationship", report.objectAndPurpose.object || "—");
    addKeyValue("Purpose of Relationship", report.objectAndPurpose.purpose || "—");
  }

  // ── 3. BENEFICIAL OWNERSHIP ─────────────────────────────
  addSection("3. Beneficial Ownership & Identity Verification");
  addParagraph(narrative.beneficialOwnership);
  if ((report.ubos || []).length > 0) {
    y += 2;
    (report.ubos || []).forEach((ubo, i) => {
      checkPage(20);
      doc.setFillColor(...colors.veryLight);
      doc.setDrawColor(...colors.lightGrey);
      doc.rect(ml, y, contentW, 18, "FD");
      y += 4;
      const indent = ml + 4;
      addKeyValue("Name", ubo.name, indent);
      addKeyValue("Ownership", `${ubo.ownership} · ${ubo.nationality} · ${parseFloat(ubo.ownership) < 25 ? "Shareholder (<25% — not a beneficial owner under Art. 2a para. 3 AMLA)" : "Beneficial owner (25%+)"}`, indent);
      const verifMatch = verifications.find(v => v.person_name?.toLowerCase().includes(ubo.name?.split(" ")[0]?.toLowerCase()));
      addKeyValue("ID Verification", verifMatch ? `${verifMatch.status} (Sumsub ref: ${verifMatch.applicant_id || "—"})` : "Pending", indent);
      if (ubo.sowAssessment) addKeyValue("SOW Assessment", ubo.sowAssessment, indent);
      y += 3;
    });
  }
  if ((report.directors || []).length > 0) {
    y += 2;
    setFont(9, "bold", colors.darkGrey);
    doc.text("Directors / Authorised Persons:", ml, y);
    y += 4;
    (report.directors || []).forEach(d => {
      checkPage(6);
      const name = typeof d === "object" ? d.name : d;
      const role = typeof d === "object" ? d.role : "Director";
      const verifMatch = verifications.find(v => v.person_name?.toLowerCase().includes(name?.split(" ")[0]?.toLowerCase()));
      addKeyValue(role, `${name} · ${verifMatch ? `Verified (${verifMatch.applicant_id || "—"})` : "Pending verification"}`, ml + 4);
    });
  }

  // ── 4. RISK ASSESSMENT ──────────────────────────────────
  addSection("4. Risk Assessment");
  addParagraph(narrative.riskAssessment);
  y += 2;
  addKeyValue("Risk Tier", report.riskTier);
  // FinSA can be object or string
  const fc = report.finsaClassification;
  const finsaCategory = fc && typeof fc === 'object' ? (fc.category || fc.label || JSON.stringify(fc)) : String(fc || '—');
  const finsaBasis = fc && typeof fc === 'object' ? fc.basis : null;
  const finsaAssessment = fc && typeof fc === 'object' ? fc.assessment : null;
  addKeyValue("FinSA Classification", finsaCategory);
  if (finsaBasis) addKeyValue("Basis", finsaBasis);
  // Add FinSA explanation paragraph
  y += 2;
  const finsaExplanation = finsaAssessment || (finsaCategory.toLowerCase().includes('professional') 
    ? "Client is classified as a Professional Client under FinSA Article 4. This classification is based on the entity's status as a regulated financial intermediary operating under FCA oversight through its contracted UK Appointed Representative arrangement. As a Professional Client, the entity is deemed to have the necessary knowledge and experience to understand the risks associated with the financial services and instruments offered by Sage Capital Management. This classification has been confirmed by the MLRO and is consistent with the entity's business profile and operational sophistication."
    : 'FinSA classification confirmed by MLRO at onboarding.');
  setFont(9, "italic", colors.darkGrey);
  const finsaLines = doc.splitTextToSize(finsaExplanation, contentW);
  checkPage(finsaLines.length * 5 + 4);
  doc.text(finsaLines, ml, y);
  y += finsaLines.length * 5 + 4;
  addKeyValue("Review Period", report.reviewPeriod);

  // ── 5. SOURCE OF FUNDS ──────────────────────────────────
  addSection("5. Source of Funds");
  addParagraph(narrative.sourceOfFunds);
  if (report.sofAssessment) {
    y += 2;
    addKeyValue("Funding Route", report.sofAssessment.fundingRoute);
    addKeyValue("Expected Volumes", report.sofAssessment.expectedVolumes || report.sofAssessment.monthlyVolume);
    addKeyValue("Assessment", report.sofAssessment.assessment);
  }

  // ── 6. SOURCE OF WEALTH ─────────────────────────────────
  if ((report.ubos || []).some(u => parseFloat(u.ownership) >= 25)) {
    addSection("6. Source of Wealth (UBOs ≥ 25%)");
    addParagraph(narrative.sourceOfWealth);
    (report.ubos || []).filter(u => parseFloat(u.ownership) >= 25).forEach(ubo => {
      checkPage(20);
      y += 2;
      setFont(9, "bold", colors.darkGrey);
      doc.text(`${ubo.name} (${ubo.ownership}):`, ml, y);
      y += 5;

      // Pull from SOW session if available
      const sowData = sowSession?.report;
      const narrative_sow = sowData?.wealthOrigins || sowData?.sowNotes || ubo.sowNarrative;
      const assessment = sowData?.sowAssessment || ubo.sowAssessment || "—";
      const evidence = sowData?.evidenceProvided || ubo.evidenceProvided;
      const mlroActions = sowData?.mlroActions || [];

      addKeyValue("Assessment", assessment, ml + 4);
      if (narrative_sow) addKeyValue("Wealth Narrative", narrative_sow, ml + 4);
      if (ubo.sowMlroRationale) addKeyValue("MLRO Rationale", ubo.sowMlroRationale, ml + 4);
      if (evidence) addKeyValue("Evidence Provided", evidence, ml + 4);
      if (sowData?.wealthTimeframe) addKeyValue("Timeframe", sowData.wealthTimeframe, ml + 4);
      if (sowData?.wealthStructure) addKeyValue("Held", sowData.wealthStructure, ml + 4);
      if (mlroActions.length > 0) {
        y += 2;
        setFont(8, "bold", colors.darkGrey);
        doc.text("MLRO Actions:", ml + 4, y); y += 4;
        mlroActions.forEach(action => {
          checkPage(6);
          setFont(8, "normal", colors.darkGrey);
          const lines = doc.splitTextToSize(`• ${action}`, contentW - 12);
          lines.forEach(line => { doc.text(line, ml + 8, y); y += 4; });
        });
      }
    });
  }

  // ── 7. AML SCREENING ────────────────────────────────────
  addSection("7. AML Screening");
  addParagraph(narrative.amlScreening);
  if (walletScreenings.length > 0) {
    y += 2;
    setFont(9, "bold", colors.darkGrey);
    doc.text("Blockchain Analytics (Chainalysis):", ml, y); y += 5;
    walletScreenings.forEach(w => {
      checkPage(10);
      const addr = w.address || w.wallet_address || "";
      const risk = w.risk_level || w.risk_rating || "—";
      const score = w.risk_score !== undefined ? ` · Score: ${w.risk_score}/10` : "";
      const asset = w.asset || "";
      const riskColor = risk.toLowerCase() === "low" ? [34, 139, 34] : risk.toLowerCase() === "medium" ? [200, 100, 0] : risk.toLowerCase() === "high" ? [180, 0, 0] : colors.darkGrey;
      setFont(8, "bold", colors.darkGrey);
      doc.text(`${asset} — ${addr.slice(0, 20)}...${addr.slice(-8)}`, ml + 4, y); y += 4.5;
      setFont(8, "normal", colors.darkGrey);
      doc.setTextColor(...riskColor);
      doc.text(`Risk: ${risk}${score} · ✓ Screened`, ml + 8, y);
      doc.setTextColor(...colors.darkGrey);
      if (w.cluster_name) { y += 4; doc.text(`Cluster: ${w.cluster_name}`, ml + 8, y); }
      y += 5;
    });
  } else if ((report.walletAddresses || session.collected_data?.walletAddresses || []).length > 0) {
    y += 2;
    setFont(9, "normal", colors.darkGrey);
    doc.text("Wallet addresses provided — blockchain analytics screening pending.", ml, y); y += 5;
  }

  // ── 8. DOCUMENTS ON FILE ────────────────────────────────
  addSection("8. Documents on File");
  checkPage(10);
  // Table header
  doc.setFillColor(...colors.accent);
  doc.rect(ml, y, contentW, 7, "F");
  setFont(8, "bold", colors.white);
  doc.text("Document", ml + 2, y + 4.5);
  doc.text("Type", ml + 80, y + 4.5);
  doc.text("Status", ml + 130, y + 4.5);
  y += 7;

  const allDocs = narrative.documentsOnFile?.length > 0 ? narrative.documentsOnFile :
    documents.map(d => ({ name: d.document_name, type: d.document_type || "—", status: d.assessment || "Received" }));

  allDocs.forEach((d, i) => {
    checkPage(7);
    if (i % 2 === 0) {
      doc.setFillColor(...colors.veryLight);
      doc.rect(ml, y, contentW, 6, "F");
    }
    setFont(8, "normal", colors.black);
    const nameLines = doc.splitTextToSize(d.name || "—", 75);
    doc.text(nameLines[0], ml + 2, y + 4);
    doc.text(String(d.type || "—").slice(0, 25), ml + 80, y + 4);
    const statusColor = (d.status || "").toLowerCase().includes("satisf") || (d.status || "").toLowerCase().includes("receiv") ? [34, 139, 34] : colors.darkGrey;
    doc.setTextColor(...statusColor);
    doc.text(String(d.status || "Received").slice(0, 20), ml + 130, y + 4);
    doc.setTextColor(...colors.black);
    y += 6;
  });
  y += 3;

  // ── 9. RISK FACTORS & MITIGANTS ─────────────────────────
  addSection("9. Risk Factors & Mitigants");
  addParagraph(narrative.riskFactors);

  // ── 10. ROSS'S RECOMMENDATION ───────────────────────────
  addSection("10. Compliance Manager Recommendation");
  addParagraph(narrative.recommendation);
  if (narrative.conditions) {
    y += 2;
    addParagraph(`Conditions: ${narrative.conditions}`, "Conditions attached to approval");
  }

  // ── 11. CASE NOTES SUMMARY ──────────────────────────────
  addSection("11. Key Compliance Actions");
  checkPage(10);
  setFont(9, "normal", colors.darkGrey);
  const keyNotes = [];
  if (session.approved_at) keyNotes.push(`Client approved: ${new Date(session.approved_at).toLocaleDateString("en-GB")} by ${session.mlro_decision}`);
  if (session.next_review_date) keyNotes.push(`Next periodic review due: ${new Date(session.next_review_date).toLocaleDateString("en-GB")}`);
  if (session.last_review_date) keyNotes.push(`Last review completed: ${new Date(session.last_review_date).toLocaleDateString("en-GB")} by ${session.last_review_by}`);
  keyNotes.forEach(note => { checkPage(5); doc.text(`• ${note}`, ml, y); y += 5; });
  y += 4;

  // ── FOOTER on all pages ─────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(...colors.veryLight);
    doc.rect(0, H - 12, W, 12, "F");
    doc.setDrawColor(...colors.lightGrey);
    doc.line(0, H - 12, W, H - 12);
    setFont(7, "normal", colors.midGrey);
    doc.text("CONFIDENTIAL — Client Due Diligence Report · Sage Capital Management AG", ml, H - 5);
    doc.text(`Page ${p} of ${totalPages} · Ref: ${ref}`, W - mr - doc.getTextWidth(`Page ${p} of ${totalPages} · Ref: ${ref}`), H - 5);
  }

  if (returnBlob) return doc.output("blob");
  const filename = `Sage_${(session.company_name || "Client").replace(/\s+/g, "_")}_Acceptance_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return null;
}
